import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';

import { Months } from '../../../../../../common-utils/formatters';
import type { AsyncStatus } from '../../../../../@types/AsyncStatus';
import type { RootState } from '../../../../../store/store';
import { getCoproSoonExpired } from '../../../../../utils/api';
import { JWTHeader } from '../../../../../utils/JWTHeader';
import tailwindConfig from '../../../../../utils/tailwindColors';
import Badge from '../../../../common/Badge';
import { showToast } from '../../../../common/Notification';
import Popup from '../../../../common/Popup';
import RedirectionCoproButtons from '../../../../common/RedirectionCoproButtons';
import RedirectionMapButtons from '../../../../common/RedirectionMapButtons';
import Table from '../../../../common/Table';

const headers = ['MOIS', 'NB COPROS', 'DETAILS'];

function CoproExpired() {
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [rnicCoproTableData, setRnicCoproTableData] = useState<any[]>(null);
  const [popupContent, setPopupContent] = useState<{ monthSelected: string, data: any[] }>(null);
  const [monthsAhead, setMonthsAhead] = useState<number>(6);

  useEffect(() => {
    if (!company || !jwt || !company.id) {return;}
    setStatus('PENDING');
    fetch(getCoproSoonExpired(company.id, monthsAhead), JWTHeader(jwt))
      .then((response) => {
        if (!response.ok) {
          throw new Error('Internal server error');
        }
        return response.json();
      })
      .then((result) => {
        const datas = Object.entries(result as Record<string, Array<any>>).map(([month, data]) => ({
          'NB COPROS': data.length,
          MOIS: `${Months[new Date(month).getMonth()]} ${new Date(month).getFullYear()}`,
          DETAILS: <button className="align-center w-full rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md md:w-auto" onClick={() => setPopupContent({ monthSelected: month, data })}>Voir le détail</button>,
        }));
        setRnicCoproTableData(datas);
        setStatus('SUCCESS');
      })
      .catch((response_error) => {
        console.error('Error fetching sales data :', response_error);
        showToast('error', 'Une erreur est survenue lors du chargement des données.');
        setStatus('ERROR');
      });
  }, [company, jwt, monthsAhead]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const handleMonthsChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setMonthsAhead(Number(event.target.value));
  };

  return (
    <div className="w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="bg-rgpd-blue px-4 py-3.5 text-center">
        <h2 className="text-xs font-semibold tracking-wider text-white uppercase">Copropriétés dont les mandats prennent fin dans les prochains mois</h2>
      </div>
      <div className="p-4">
        <label htmlFor="monthsSelect" className="mr-2 text-sm font-medium text-gray-700">Période de prévision :</label>
        <select
          id="monthsSelect"
          value={monthsAhead}
          onChange={handleMonthsChange}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-sm transition-all duration-200 hover:border-gray-300 focus:border-rgpd-blue focus:ring-2 focus:ring-rgpd-blue/20 focus:outline-none"
        >
          {Array.from({ length: 11 }, (_, i) => i + 2).map((num) => (
            <option key={num} value={num}>
              {num}
              {' '}
              mois
            </option>
          ))}
        </select>
      </div>
      <div>
        {status === 'PENDING' && (
          <div className="w-full pb-2 text-center">
            <Table data={[]} headers={headers} autoresize={false} tableClass="w-full mb-1" />
            <Skeleton count={6} height={30} highlightColor={`${tailwindConfig.theme.extend.colors['rgpd-blue']}45`} className="my-1" />
          </div>
        )}
        {status === 'ERROR'
          && (
            <div className="mx-10 my-32 text-center">
              <Badge
                mode="error"
                text="Erreur lors du chargement des données"
              />
            </div>
          )}
        {status === 'SUCCESS'
          && (
            <Table
              data={rnicCoproTableData}
              headers={headers}
              idKey="MOIS"
            />
          )}
      </div>
      {popupContent && (
        <Popup
          isOpen
          title={`Détails des copropriétés dont le dernier mandat prend fin en ${Months[(new Date(popupContent.monthSelected).getMonth())]} ${(new Date(popupContent.monthSelected).getFullYear()).toString()}`}
          onClose={() => setPopupContent(null)}
        >
          <Table
            data={popupContent.data.map((item, index) => {
              // Compute the formatted address
              const numeroEtVoie = (item.details.numeroEtVoieAdresseDeReference || '').trim();
              const codePostal = (item.details.codePostalAdresseDeReference || '').trim();
              const commune = (item.details.communeAdresseDeReference || '').trim();
              const formattedAddress = `${numeroEtVoie} ${codePostal} ${commune}`.trim();
              const addressDisplay = formattedAddress || 'N/A';

              return {
                id: `coproExpired-Popup-${index}`,
                'Immatriculation': item.details.numeroDimmatriculation || 'N/A',
                Nom: <div className="flex items-center">
                  <RedirectionCoproButtons coproId={item.id} />
                  {item.details.nomDusageDeLaCopropriete}
                </div>,
                Adresse: addressDisplay === 'N/A' ? (
                  <span>{addressDisplay}</span>
                ) : (
                  <div className="flex items-center">
                    <div>
                      <RedirectionMapButtons address={formattedAddress} />
                    </div>
                    <span>{addressDisplay}</span>
                  </div>
                ),
                'Date de début de mandat': item.details.detailsInforattachementDatedebutmandat ? formatDate(item.details.detailsInforattachementDatedebutmandat) : 'N/A',
                'Date de dernière AG': item.details.detailsDonneesfinancieresDateag ? formatDate(item.details.detailsDonneesfinancieresDateag) : 'N/A',
                'Date de fin de mandat': item.details.dateDeFinDuDernierMandat ? formatDate(item.details.dateDeFinDuDernierMandat) : 'N/A',
              };
            })}
            headers={[
              'Immatriculation',
              'Nom',
              'Adresse',
              'Date de début de mandat',
              'Date de dernière AG',
              'Date de fin de mandat',
            ]}
            idKey="id"
          />
          {popupContent.data.length === 0 && (
            <div className="mt-10 mb-6 text-center">
              <Badge
                mode="warning"
                text="Aucune copropriété en fin de mandat pour cette période."
              />
            </div>
          )}
        </Popup>
      )}
    </div>
  );
}

export default CoproExpired;
